import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useReducer, useContext, useCallback, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { VerticalSeparatedLayout } from '@bingads-webui-react/steps';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getTagTemplates } from '@bingads-webui-campaign-react/goal-creation-wizard';
import { UetSetupWizardContext } from './uet-setup-wizard-context';
import { uetSetupWizardReducer, WIZARD_ACTIONS, defaultState } from './uet-setup-wizard-reducer';
import { instrumentationSchema } from './shared/instrumentation-schema';
import { startActivity, endActivity } from './shared/util';
import { getStyles } from './styles/get-styles';
import { STEP_PATHS } from './step-paths';
import {
  CreateUetTagStep,
  TagSetupMethodStep,
  TagSetupInstallManuallyStep,
  TagSetupSendToDeveloperStep,
  TagSetupThirdPartyPlatformStep,
  TagSetupThirdPartyInstructionStep,
  GtmBaseTagSetupStep,
  FinishStep,
} from './steps';

const UetSetupWizardUnstyled = (props) => {
  const {
    classes,
    instrumentation,
    onWizardExit,
    legacyI18n,
    tagEditEntity,
    customerHierarchy,
    permissions,
    scope,
    serverContext,
    registerBackButtonClickedHandler,
  } = props;

  const {
    StepsContextProvider,
    StepContainer,
    Step,
    StepProgressList,
  } = VerticalSeparatedLayout;

  const tagEditState = { tagContext: tagEditEntity ? _.pick(tagEditEntity, ['Id', 'Name', 'Description', 'IsClarityTagEnabled']) : {} };
  const defaultWizardState = { ...defaultState, ...tagEditState };

  const [state, dispatch] = useReducer(uetSetupWizardReducer, defaultWizardState);
  const i18n = useContext(I18nContext);

  const isUetSpaTrackingEnabled = _.result(permissions.dynamic, 'UetAutoSpaTracking');
  const tagTemplates = useMemo(() => getTagTemplates(i18n, isUetSpaTrackingEnabled), [i18n, isUetSpaTrackingEnabled]);

  useMemo(() => instrumentation.addScenario(instrumentationSchema), [instrumentation]);

  const logScenario = useMemo(() => {
    const scenario = instrumentation.uetSetupWizard.create();

    return scenario;
  }, [instrumentation.uetSetupWizard]);

  const logActivity = useMemo(() => {
    const activity = logScenario[state.currentStep].create();

    startActivity(activity);
    return activity;
  }, [logScenario, state.currentStep]);

  const goNext = useCallback(() => {
    endActivity(logActivity);
    dispatch({ type: WIZARD_ACTIONS.WIZARD_NAVIGATE_NEXT });
  }, [logActivity]);

  const goBack = useCallback(() => {
    endActivity(logActivity);
    dispatch({ type: WIZARD_ACTIONS.WIZARD_NAVIGATE_BACK });
  }, [logActivity]);

  const wizardContext = useMemo(() => ({
    goNext,
    goBack,
    dispatch,
    legacyI18n,
    scope,
    permissions,
    serverContext,
    logActivity,
    isEdit: tagEditEntity && !!tagEditEntity.Id,
    tagContext: state.tagContext,
    GTMContext: state.GTMContext,
    tagTemplates,
  }), [goBack, goNext, legacyI18n, logActivity, permissions, scope, serverContext, state.GTMContext, state.tagContext, tagEditEntity, tagTemplates]);

  const logSuccess = useCallback(() => {
    const logObject = {
      isEditScenario: wizardContext.isEdit,
      tagId: wizardContext.tagContext.Id,
      tagContext: wizardContext.tagContext,
      currentStep: state.currentStep,
      isComplete: true,
      stepHistory: state.loggingSteps,
    };
    logActivity.trace(`Wizard workflow complete log: ${JSON.stringify(logObject)}`);
  }, [logActivity, state.currentStep, state.loggingSteps, wizardContext.isEdit, wizardContext.tagContext]);

  const logFailure = useCallback(() => {
    const logObject = {
      isEditScenario: wizardContext.isEdit,
      tagId: wizardContext.tagContext.Id,
      tagContext: wizardContext.tagContext,
      currentStep: state.currentStep,
      isComplete: false,
      isSaveDone: !!state.isSaveDone,
      stepHistory: state.loggingSteps,
    };
    logActivity.trace(`Wizard workflow incomplete log: ${JSON.stringify(logObject)}`);
  }, [logActivity, state.currentStep, state.isSaveDone, state.loggingSteps, wizardContext.isEdit, wizardContext.tagContext]);

  wizardContext.exitWizard = useCallback((...args) => {
    const isDoneClicked = args.length > 0 && args[0] === true;

    if (isDoneClicked) {
      logSuccess();
    } else {
      logFailure();
    }
    endActivity(logActivity);
    onWizardExit();
  }, [logActivity, logFailure, logSuccess, onWizardExit]);

  registerBackButtonClickedHandler(() => {
    logFailure();
  });

  const getStepProgressItemGlyphicon = useCallback((step) => {
    if (step.path.indexOf('.') >= 0) {
      return 'ba-RadioBullet';
    }

    if (step.isSelected || step.containsSelected) {
      return 'ba-RadioBullet2';
    } else if (step.isBehind) {
      return 'ba-CompletedSolid';
    }

    return 'ba-RadioBtnOff';
  }, []);

  return (
    <div className={classNames(classes.uetSetupWizard)}>
      <UetSetupWizardContext.Provider value={wizardContext}>
        <StepsContextProvider currentPath={state.currentStep}>
          <div id="goal-creation-wizard-steps-content" className={classes.wizardContentContainer}>
            <div id="goal-creation-wizard-steps-list" className={classes.wizardListContainer}>
              <StepProgressList getItemGlyphicon={getStepProgressItemGlyphicon} />
            </div>
            <StepContainer>
              <Step
                title={i18n.getString(_TL_('Create UET tag'))}
                path={STEP_PATHS.uetTag.id}
              >
                <CreateUetTagStep
                  legacyI18n={legacyI18n}
                  permissions={permissions}
                  tagEditEntity={tagEditEntity}
                  customerHierarchy={customerHierarchy}
                />
              </Step>
              <Step
                title={i18n.getString(_TL_('Set up tagging'))}
                path={STEP_PATHS.tagSetup.id}
              >
                <StepContainer subContainer>
                  <Step path={STEP_PATHS.tagSetup.noTag.id}>
                    <StepContainer subContainer>
                      <Step
                        path={STEP_PATHS.tagSetup.noTag.selectTagMethod.id}
                      >
                        <TagSetupMethodStep
                          selectedItem={state.selectedTagSetupMethod}
                        />
                      </Step>
                      <Step
                        path={STEP_PATHS.tagSetup.noTag.installTag.id}
                      >
                        <TagSetupInstallManuallyStep />
                      </Step>
                      <Step
                        path={STEP_PATHS.tagSetup.noTag.emailTag.id}
                      >
                        <TagSetupSendToDeveloperStep />
                      </Step>
                      <Step
                        path={STEP_PATHS.tagSetup.noTag.platformTag.id}
                      >
                        <TagSetupThirdPartyPlatformStep />
                      </Step>
                      <Step
                        path={STEP_PATHS.tagSetup.noTag.platformTagInstructions.id}
                      >
                        <TagSetupThirdPartyInstructionStep
                          selectedPlatform={state.selectedPlatform}
                        />
                      </Step>
                      <Step
                        path={STEP_PATHS.tagSetup.noTag.gtmTag.id}
                      >
                        <GtmBaseTagSetupStep
                          selectedPlatform="GoogleTagManager"
                          serverContext={serverContext}
                          onWorkflowExit={wizardContext.exitWizard}
                        />
                      </Step>
                    </StepContainer>
                  </Step>
                </StepContainer>
              </Step>
              <Step
                title={i18n.getString(_TL_('Next steps'))}
                path={STEP_PATHS.endFlow.id}
              >
                <FinishStep permissions={permissions} serverContext={serverContext} />
              </Step>
            </StepContainer>
            <iframe title="logoutIFrame" name="logoutIFrame" style={{ display: 'none' }} />
          </div>
        </StepsContextProvider>
      </UetSetupWizardContext.Provider>
    </div>
  );
};

UetSetupWizardUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(getStyles(), _.constant(PropTypes.string.isRequired)))
    .isRequired,
  instrumentation: PropTypes.objectOf(PropTypes.any).isRequired,
  onWizardExit: PropTypes.func.isRequired,
  legacyI18n: PropTypes.objectOf(PropTypes.any).isRequired,
  tagEditEntity: PropTypes.objectOf(PropTypes.any),
  customerHierarchy: PropTypes.arrayOf(PropTypes.any),
  scope: PropTypes.objectOf(PropTypes.any).isRequired,
  permissions: PropTypes.objectOf(PropTypes.any).isRequired,
  serverContext: PropTypes.objectOf(PropTypes.any).isRequired,
  registerBackButtonClickedHandler: PropTypes.func.isRequired,
};

UetSetupWizardUnstyled.defaultProps = {
  tagEditEntity: null,
  customerHierarchy: null,
};

export const UetSetupWizard = withInstrumentation(withDefaultStyles(UetSetupWizardUnstyled, getStyles));
